import React, { useEffect, useState, useMemo } from "react";
import {
  motion,
  AnimatePresence,
  useMotionValue,
  animate,
  useTransform,
} from "framer-motion";
import "./MapAnimation.css";

// --- ISOMETRIC MATH HELPERS ---
const TILE_SIZE = 60;
const ISO_ANGLE = 30 * (Math.PI / 180);

const toScreen = (gridX, gridZ, height = 0) => {
  const x = (gridX - gridZ) * TILE_SIZE * Math.cos(ISO_ANGLE);
  const y = (gridX + gridZ) * TILE_SIZE * Math.sin(ISO_ANGLE) - height;
  return { x, y };
};

const MapAnimation = () => {
  const [status, setStatus] = useState("idle");
  const [labelData, setLabelData] = useState(null);

  const progress = useMotionValue(0);

  // --- CITY CONFIGURATION ---
  const buildings = useMemo(
    () =>
      [
        { id: 1, x: 0, z: 0, w: 1.5, d: 1.5, h: 120, type: "glass-tower" },
        { id: 2, x: 3.5, z: 0, w: 2, d: 1.5, h: 90, type: "concrete" },
        { id: 3, x: 8, z: 0, w: 2, d: 1.5, h: 110, type: "glass-tower" },
        { id: 4, x: 3.5, z: 3, w: 2.5, d: 1.5, h: 60, type: "warehouse" },
        { id: 5, x: 0, z: 6, w: 1.5, d: 2, h: 50, type: "shop" },
        { id: 6, x: 3.5, z: 6.5, w: 2, d: 1.5, h: 70, type: "shop" },
        { id: 7, x: 8.5, z: 3, w: 1.5, d: 1.5, h: 140, type: "concrete" },
      ].sort((a, b) => a.x + a.z - (b.x + b.z)),
    [],
  );

  const waypoints = [
    { x: -1, z: 2 },
    { x: 2, z: 2 }, // Intersection 1
    { x: 2, z: 5 }, // Intersection 2 (Hazard)
    { x: 7, z: 5 }, // Intersection 3 (Safe)
    { x: 7, z: 9 }, // Exit
  ];

  // --- ANIMATION SEQUENCE ---
  useEffect(() => {
    let isMounted = true;
    const sequence = async () => {
      while (isMounted) {
        progress.set(0);
        setStatus("moving");
        setLabelData(null);

        // 1. Move to first zone approach
        await animate(progress, 0.26, { duration: 2.5, ease: "linear" });

        // 2. Alert / Hazard Zone
        setStatus("alert"); // Triggers Zone Visuals
        setLabelData({ text: "entered_geofence", type: "alert", x: 2, z: 5 });

        // Move slowly through zone
        await animate(progress, 0.48, { duration: 2.5, ease: "linear" });

        setLabelData(null);
        setStatus("moving");

        // 3. Move to Safe Destination
        await animate(progress, 0.77, { duration: 2.5, ease: "linear" });
        setStatus("arrived");
        setLabelData({ text: "entered_geofence", type: "success", x: 7, z: 5 });

        await new Promise((r) => setTimeout(r, 1500));
        setLabelData(null);

        // Exit
        await animate(progress, 1, { duration: 2, ease: "easeInOut" });
        setStatus("idle");
        await new Promise((r) => setTimeout(r, 1000));
      }
    };
    sequence();
    return () => {
      isMounted = false;
    };
  }, [progress]);

  return (
    <div className="iso-map-wrapper">
      <div className="iso-container">
        <svg viewBox="-500 -200 1000 800" className="iso-svg">
          <defs>
            <linearGradient id="wallLeft" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#94a3b8" />
              <stop offset="100%" stopColor="#cbd5e1" />
            </linearGradient>
            <linearGradient id="wallRight" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#e2e8f0" />
              <stop offset="100%" stopColor="#cbd5e1" />
            </linearGradient>
            <linearGradient
              id="glassGradient"
              x1="0%"
              y1="0%"
              x2="0%"
              y2="100%"
            >
              <stop offset="0%" stopColor="rgba(56, 189, 248, 0.3)" />
              <stop offset="100%" stopColor="rgba(56, 189, 248, 0.1)" />
            </linearGradient>
            <filter
              id="softShadow"
              x="-50%"
              y="-50%"
              width="200%"
              height="200%"
            >
              <feGaussianBlur in="SourceAlpha" stdDeviation="4" />
              <feOffset dx="4" dy="4" result="offsetblur" />
              <feComponentTransfer>
                <feFuncA type="linear" slope="0.3" />
              </feComponentTransfer>
              <feMerge>
                <feMergeNode />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
            <filter id="glow-light">
              <feGaussianBlur stdDeviation="2.5" result="coloredBlur" />
              <feMerge>
                <feMergeNode in="coloredBlur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          <IsoGrid width={12} depth={12} />

          <IsoRoad x={-2} z={2} length={14} axis="x" />
          <IsoRoad x={1} z={5} length={8} axis="x" />
          <IsoRoad x={2} z={2} length={8} axis="z" />
          <IsoRoad x={7} z={2} length={10} axis="z" />

          <IsoRoutePath waypoints={waypoints} color="#00f2fe" />

          {/* ZONES - Moved here to be ON TOP of roads */}
          <IsoVolumetricZone
            x={2}
            z={5}
            w={3}
            d={3}
            color="#f97316"
            label="ZONE 01"
            active={status === "alert"}
          />
          <IsoVolumetricZone
            x={7}
            z={5}
            w={3}
            d={3}
            color="#3b82f6"
            label="ZONE 02"
            active={status === "arrived"}
          />

          {buildings.map((b) => (
            <IsoBuilding key={b.id} {...b} />
          ))}

          <IsoVehicle
            path={[
              { x: -2, z: 2 },
              { x: 12, z: 2 },
            ]}
            duration={10}
            delay={0}
            color="#64748b"
          />
          <IsoVehicle
            path={[
              { x: 7, z: -2 },
              { x: 7, z: 12 },
            ]}
            duration={15}
            delay={5}
            color="#64748b"
          />

          {/* HERO LOCATION MARKER */}
          <HeroMarker progress={progress} waypoints={waypoints} />

          <AnimatePresence>
            {labelData && (
              <IsoLabel
                x={labelData.x}
                z={labelData.z}
                text={labelData.text}
                type={labelData.type}
              />
            )}
          </AnimatePresence>
        </svg>
      </div>
    </div>
  );
};

// --- COMPONENTS ---

// New Volumetric Zone with "Force Field" walls
const IsoVolumetricZone = ({ x, z, w, d, color, active, label }) => {
  const p1 = toScreen(x - w / 2, z - d / 2);
  const p2 = toScreen(x + w / 2, z - d / 2);
  const p3 = toScreen(x + w / 2, z + d / 2);
  const p4 = toScreen(x - w / 2, z + d / 2);

  // Top of the volume walls (height 40)
  const H = 40;
  const p1h = { x: p1.x, y: p1.y - H };
  const p2h = { x: p2.x, y: p2.y - H };
  const p3h = { x: p3.x, y: p3.y - H };
  const p4h = { x: p4.x, y: p4.y - H };

  return (
    <g>
      {/* Base */}
      <motion.path
        d={`M ${p1.x} ${p1.y} L ${p2.x} ${p2.y} L ${p3.x} ${p3.y} L ${p4.x} ${p4.y} Z`}
        fill={color}
        initial={{ opacity: 0.1 }}
        animate={{ opacity: active ? 0.3 : 0.1 }}
        stroke={color}
        strokeWidth={active ? 2 : 1}
        strokeDasharray="4 2"
      />
      {/* Label on Floor */}
      {active && (
        <text
          x={(p1.x + p3.x) / 2}
          y={(p1.y + p3.y) / 2}
          fill={color}
          fontSize="10"
          fontWeight="bold"
          textAnchor="middle"
          opacity="0.8"
          style={{ pointerEvents: "none" }}
        >
          {label}
        </text>
      )}

      {/* Rising Walls (Animate Height/Opacity) */}
      <AnimatePresence>
        {active && (
          <motion.g
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {/* Back Walls */}
            <path
              d={`M ${p1.x} ${p1.y} L ${p2.x} ${p2.y} L ${p2h.x} ${p2h.y} L ${p1h.x} ${p1h.y} Z`}
              fill={color}
              opacity="0.1"
            />
            <path
              d={`M ${p1.x} ${p1.y} L ${p4.x} ${p4.y} L ${p4h.x} ${p4h.y} L ${p1h.x} ${p1h.y} Z`}
              fill={color}
              opacity="0.1"
            />

            {/* Front Walls */}
            <motion.path
              d={`M ${p4.x} ${p4.y} L ${p3.x} ${p3.y} L ${p3h.x} ${p3h.y} L ${p4h.x} ${p4h.y} Z`}
              fill={color}
              opacity="0.15"
              animate={{ opacity: [0.1, 0.25, 0.1] }}
              transition={{ duration: 2, repeat: Infinity }}
            />
            <motion.path
              d={`M ${p3.x} ${p3.y} L ${p2.x} ${p2.y} L ${p2h.x} ${p2h.y} L ${p3h.x} ${p3h.y} Z`}
              fill={color}
              opacity="0.15"
              animate={{ opacity: [0.1, 0.25, 0.1] }}
              transition={{ duration: 2, repeat: Infinity, delay: 1 }}
            />

            {/* Top Outline */}
            <path
              d={`M ${p1h.x} ${p1h.y} L ${p2h.x} ${p2h.y} L ${p3h.x} ${p3h.y} L ${p4h.x} ${p4h.y} Z`}
              fill="none"
              stroke={color}
              strokeWidth="1"
              opacity="0.5"
            />
          </motion.g>
        )}
      </AnimatePresence>
    </g>
  );
};

// Replaces HeroVehicle
const HeroMarker = ({ progress, waypoints }) => {
  const x = useTransform(progress, (p) => getInterpolatedPos(p, waypoints).x);
  const y = useTransform(progress, (p) => getInterpolatedPos(p, waypoints).y);

  return (
    <motion.g style={{ x, y }} className="hero-marker">
      {/* Pulsing Ring on Ground */}
      <motion.ellipse
        cx="0"
        cy="0"
        rx={15}
        ry={8}
        fill="none"
        stroke="#38bdf8"
        strokeWidth="2"
        initial={{ rx: 10, ry: 5 }}
        animate={{ rx: [10, 20, 10], ry: [5, 10, 5], opacity: [0.8, 0, 0.8] }}
        transition={{ duration: 1.5, repeat: Infinity }}
      />
      {/* Center Dot Shadow */}
      <ellipse
        cx="0"
        cy="0"
        rx="4"
        ry="2"
        fill="black"
        opacity="0.5"
        filter="blur(1px)"
      />

      {/* Floating Valid SVG Pin Icon */}
      <motion.g
        animate={{ y: [-15, -25, -15] }}
        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
      >
        {/* 3D Pin Shape Constructed */}
        {/* Pin Head */}
        <circle
          cx="0"
          cy="-20"
          r="14"
          fill="#0ea5e9"
          stroke="white"
          strokeWidth="2"
        />
        <circle cx="0" cy="-20" r="4" fill="white" />

        {/* Pin Needle */}
        <path d="M -6 -10 L 0 0 L 6 -10" fill="#0284c7" />
      </motion.g>
    </motion.g>
  );
};

const IsoGrid = ({ width, depth }) => {
  const lines = [];
  for (let i = 0; i <= width; i++) {
    const start = toScreen(i, -2);
    const end = toScreen(i, depth);
    lines.push(
      <path
        key={`z${i}`}
        d={`M ${start.x} ${start.y} L ${end.x} ${end.y}`}
        stroke="rgba(255,255,255,0.03)"
        strokeWidth="1"
      />,
    );
  }
  for (let j = -2; j <= depth; j++) {
    const start = toScreen(0, j);
    const end = toScreen(width, j);
    lines.push(
      <path
        key={`x${j}`}
        d={`M ${start.x} ${start.y} L ${end.x} ${end.y}`}
        stroke="rgba(255,255,255,0.03)"
        strokeWidth="1"
      />,
    );
  }
  return <g>{lines}</g>;
};

const IsoRoad = ({ x, z, length, axis }) => {
  const RW = 0.8;
  let p1, p2, p3, p4;

  if (axis === "x") {
    p1 = toScreen(x, z - RW / 2);
    p2 = toScreen(x + length, z - RW / 2);
    p3 = toScreen(x + length, z + RW / 2);
    p4 = toScreen(x, z + RW / 2);
  } else {
    p1 = toScreen(x - RW / 2, z);
    p2 = toScreen(x - RW / 2, z + length);
    p3 = toScreen(x + RW / 2, z + length);
    p4 = toScreen(x + RW / 2, z);
  }

  const height = 3;
  const p2b = { x: p2.x, y: p2.y + height };
  const p3b = { x: p3.x, y: p3.y + height };
  const p4b = { x: p4.x, y: p4.y + height };

  return (
    <g>
      <path
        d={`M ${p4.x} ${p4.y} L ${p3.x} ${p3.y} L ${p3b.x} ${p3b.y} L ${p4b.x} ${p4b.y} Z`}
        fill="#0f172a"
      />
      <path
        d={`M ${p2.x} ${p2.y} L ${p3.x} ${p3.y} L ${p3b.x} ${p3b.y} L ${p2b.x} ${p2b.y} Z`}
        fill="#0f172a"
      />
      <path
        d={`M ${p1.x} ${p1.y} L ${p2.x} ${p2.y} L ${p3.x} ${p3.y} L ${p4.x} ${p4.y} Z`}
        fill="#1e293b"
      />
      <path
        d={`M ${(p1.x + p4.x) / 2} ${(p1.y + p4.y) / 2} L ${
          (p2.x + p3.x) / 2
        } ${(p2.y + p3.y) / 2}`}
        stroke="rgba(255,255,255,0.15)"
        strokeWidth="2"
        strokeDasharray="8 8"
      />
    </g>
  );
};

const IsoRoutePath = ({ waypoints, color }) => {
  const d = waypoints
    .map((pt, i) => {
      const s = toScreen(pt.x, pt.z);
      return `${i === 0 ? "M" : "L"} ${s.x} ${s.y - 2}`;
    })
    .join(" ");

  return (
    <g filter="url(#glow-light)">
      <path
        d={d}
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeDasharray="3 3"
        opacity="0.6"
        strokeLinecap="round"
      />
    </g>
  );
};

const IsoBuilding = ({ x, z, w, d, h, type }) => {
  const v1 = toScreen(x, z);
  const v2 = toScreen(x + w, z);
  const v3 = toScreen(x + w, z + d);
  const v4 = toScreen(x, z + d);

  const v1h = { x: v1.x, y: v1.y - h };
  const v2h = { x: v2.x, y: v2.y - h };
  const v3h = { x: v3.x, y: v3.y - h };
  const v4h = { x: v4.x, y: v4.y - h };

  const isGlass = type === "glass-tower";
  const isWarehouse = type === "warehouse";

  return (
    <g className="iso-building" filter="url(#softShadow)">
      <path
        d={`M ${v3.x} ${v3.y} L ${v2.x} ${v2.y} L ${v2h.x} ${v2h.y} L ${v3h.x} ${v3h.y} Z`}
        fill={isGlass ? "url(#glassGradient)" : "url(#wallRight)"}
        stroke={isGlass ? "rgba(255,255,255,0.2)" : "none"}
      />
      <path
        d={`M ${v4.x} ${v4.y} L ${v3.x} ${v3.y} L ${v3h.x} ${v3h.y} L ${v4h.x} ${v4h.y} Z`}
        fill={isGlass ? "url(#glassGradient)" : "url(#wallLeft)"}
        stroke={isGlass ? "rgba(255,255,255,0.2)" : "none"}
      />
      <path
        d={`M ${v1h.x} ${v1h.y} L ${v2h.x} ${v2h.y} L ${v3h.x} ${v3h.y} L ${v4h.x} ${v4h.y} Z`}
        fill={isGlass ? "rgba(255,255,255,0.6)" : "#f1f5f9"}
      />
      {isWarehouse && (
        <path
          d={`M ${v4.x} ${v4.y} L ${v3.x} ${v3.y}`}
          stroke="#334155"
          strokeWidth="1"
          strokeDasharray="3 3"
        />
      )}
      {!isGlass && !isWarehouse && (
        <path
          d={`M ${v1h.x} ${v1h.y + 2} L ${v2h.x} ${v2h.y + 2} L ${v3h.x} ${
            v3h.y + 2
          } L ${v4h.x} ${v4h.y + 2} Z`}
          fill="none"
          stroke="rgba(0,0,0,0.05)"
          strokeWidth="1"
          transform="translate(0, 2)"
        />
      )}
    </g>
  );
};

const getInterpolatedPos = (p, waypoints) => {
  let t = 0;
  let start, end;

  if (p < 0.26) {
    t = p / 0.26;
    start = waypoints[0];
    end = waypoints[1];
  } else if (p < 0.48) {
    t = (p - 0.26) / (0.48 - 0.26);
    start = waypoints[1];
    end = waypoints[2];
  } else if (p < 0.77) {
    t = (p - 0.48) / (0.77 - 0.48);
    start = waypoints[2];
    end = waypoints[3];
  } else {
    t = (p - 0.77) / (1 - 0.77);
    start = waypoints[3];
    end = waypoints[4];
  }

  if (!start || !end) return { x: 0, y: 0 };
  const gx = start.x + (end.x - start.x) * t;
  const gz = start.z + (end.z - start.z) * t;
  return toScreen(gx, gz);
};

const IsoVehicle = ({ path, duration, delay, color }) => {
  return (
    <motion.g
      initial={{ offset: 0 }}
      animate={{ offset: 1 }}
      transition={{ duration, repeat: Infinity, delay, ease: "linear" }}
    >
      <CarShape
        start={path[0]}
        end={path[1]}
        duration={duration}
        delay={delay}
        color={color}
      />
    </motion.g>
  );
};

const CarShape = ({ start, end, duration, delay, color }) => {
  const s = toScreen(start.x, start.z);
  const e = toScreen(end.x, end.z);
  return (
    <g>
      <circle r="0" fill="none">
        <animate
          attributeName="r"
          from="0"
          to="1"
          dur={`${duration}s`}
          begin={`${delay}s`}
          repeatCount="indefinite"
        />
      </circle>
      <g transform={`translate(${s.x}, ${s.y})`}>
        <animateTransform
          attributeName="transform"
          type="translate"
          from={`${s.x} ${s.y}`}
          to={`${e.x} ${e.y}`}
          dur={`${duration}s`}
          begin={`${delay}s`}
          repeatCount="indefinite"
        />
        <circle r="3" fill={color} />
      </g>
    </g>
  );
};

// Color definitions for IsoLabel types
const colors = {
  info: { bg: "rgba(15, 23, 42, 0.9)", border: "#38bdf8", text: "#e2e8f0" },
  alert: {
    bg: "rgba(69, 10, 10, 0.9)",
    border: "#ef4444",
    text: "#fecaca",
  },
  success: {
    bg: "rgba(6, 78, 59, 0.9)",
    border: "#10b981",
    text: "#bbf7d0",
  },
};

const IsoLabel = ({ x, z, text, type }) => {
  const pos = toScreen(x, z, 40);
  const colorSet = colors[type] || colors.info;

  return (
    <motion.foreignObject
      x={pos.x - 60}
      y={pos.y - 60}
      width={120}
      height={60}
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.8 }}
      style={{ pointerEvents: "none" }}
    >
      <div className="flex flex-col items-center justify-end h-full">
        <div
          className="px-3 py-1.5 rounded-md shadow-xl border backdrop-blur-sm flex items-center gap-2"
          style={{
            backgroundColor: colorSet.bg,
            borderColor: colorSet.border,
          }}
        >
          <div
            style={{
              width: "6px",
              height: "6px",
              borderRadius: "50%",
              backgroundColor: colorSet.border,
              boxShadow: `0 0 5px ${colorSet.border}`,
            }}
          ></div>
          <span
            className="text-[10px] font-bold tracking-wide"
            style={{ color: colorSet.text }}
          >
            {text}
          </span>
        </div>
        <svg width="20" height="20" viewBox="0 0 20 20" className="mt-[-1px]">
          <path
            d="M 10 0 L 10 20"
            stroke={colorSet.border}
            strokeWidth="1"
            strokeDasharray="2 2"
          />
          <circle cx="10" cy="20" r="2" fill={colorSet.border} />
        </svg>
      </div>
    </motion.foreignObject>
  );
};

export default MapAnimation;
